/**
 * Page linter (builder Console, WS-3b) — a CLIENT-side, COMPILE-only check of all
 * the JS / binding surfaces on a page, so the Console's "Linter" tab catches more
 * than just JS Objects (owner feedback): JS errors in component fields are
 * surfaced too. It checks SYNTAX, not values — an undefined `$page.x` is NOT an
 * error here (that's a runtime concern), a malformed `{{ }}` or a broken action
 * body IS.
 *
 * Three surfaces:
 *   - **JS Objects** (`ast.js_objects`) — compiled via `compileJsObject`.
 *   - **Action JS** — every `run_js_inline` action's `code`, anywhere in a node's
 *     props (button on_click, row actions, …), parsed as an async function body.
 *   - **Bindings** — every `{{ … }}` expression in a string prop / `visible_when`,
 *     parsed via the page-expression parser.
 */
import { parseExpr, templateExpressions } from "@/lib/pageExpr";
import { compileJsObject } from "@/lib/pageJsObjects";
import type { PageAst, PageNode } from "@/lib/pageTypes";

export interface LintIssue {
  kind: "js-object" | "action-js" | "binding";
  /** Human label of where the issue lives (JS Object name, or a node ref). */
  where: string;
  error: string;
}

function msg(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

/** Syntax-check an inline-JS action body (`run_js_inline.code`). Wrapped as an
 *  async function body so `await` is legal; `new Function` PARSES without running,
 *  so undefined names don't false-positive. */
function inlineJsError(code: string): string | null {
  try {
    new Function(`"use strict"; return (async function(){\n${code}\n});`);
    return null;
  } catch (e) {
    return msg(e);
  }
}

/** Syntax-check every `{{ … }}` expression in a template string. */
function bindingErrors(tpl: string): string[] {
  const out: string[] = [];
  for (const expr of templateExpressions(tpl)) {
    try {
      parseExpr(expr);
    } catch (e) {
      out.push(msg(e));
    }
  }
  return out;
}

/** Recursively scan a value (a node's props tree) for action-JS + bindings. */
function scanValue(v: unknown, where: string, issues: LintIssue[]): void {
  if (typeof v === "string") {
    if (v.includes("{{")) {
      for (const error of bindingErrors(v)) issues.push({ kind: "binding", where, error });
    }
    return;
  }
  if (Array.isArray(v)) {
    for (const x of v) scanValue(x, where, issues);
    return;
  }
  if (v && typeof v === "object") {
    const o = v as Record<string, unknown>;
    if (o.type === "run_js_inline" && typeof o.code === "string") {
      const error = inlineJsError(o.code);
      if (error) issues.push({ kind: "action-js", where, error });
    }
    for (const x of Object.values(o)) scanValue(x, where, issues);
  }
}

function walk(node: PageNode, issues: LintIssue[]): void {
  const where = `${node.type}#${node.id}`;
  scanValue(node.props ?? {}, where, issues);
  if (node.visible_when) scanValue(node.visible_when, where, issues);
  for (const child of node.children ?? []) walk(child, issues);
}

/** Lint the whole page: JS Objects + every node's action-JS + bindings. */
export function lintPage(ast: PageAst): LintIssue[] {
  const issues: LintIssue[] = [];
  for (const o of ast.js_objects ?? []) {
    const c = compileJsObject(o.code);
    if ("error" in c) issues.push({ kind: "js-object", where: o.name, error: c.error });
  }
  walk(ast.root, issues);
  return issues;
}
